import React from 'react'
import { Box, Icon, Highlight, Flex, Text, Tag } from '@chakra-ui/react'
import { FaPaw } from 'react-icons/fa'
import { FiUser } from 'react-icons/fi'
import { get as leven } from 'fast-levenshtein'

import {
    PatientPreviewFieldsFragment,
    ClinicFieldsFragment,
    QueryPatientsOwnersQuery,
} from '~graphql/generated/graphql'
import {
    formatEmail,
    getAlivePatients,
    getClientFullName,
    getClientSecondaryName,
} from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import { useClinic } from '~contexts/clinic-context'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { FaEnvelope, FaPhone } from 'react-icons/fa6'
import ExportRemindersButton from './export-reminders-button'

type ClientSearchResultProps = Readonly<{
    res: QueryPatientsOwnersQuery['queryPatientsOwners']['patients'][0]
    value: string
    onClick: (patient: PatientPreviewFieldsFragment) => void
    clinic: ClinicFieldsFragment
    showReminderPdfAction: boolean
}>

export default function PatientSearchResult({
    res,
    value,
    onClick,
    clinic,
    showReminderPdfAction,
}: ClientSearchResultProps) {
    const patient = res

    if (!patient) return <></>

    const isInactive = !patient.is_active || patient.is_deceased

    const clientSecondaryName = patient.owner
        ? getClientSecondaryName(patient.owner)
        : undefined

    return (
        <Box
            key={patient.id}
            role="button"
            tabIndex={0}
            w="100%"
            px={4}
            py={2}
            borderRadius={8}
            _hover={{
                bg: 'gray.100',
            }}
            opacity={isInactive ? 0.8 : 1}
            onClick={() => onClick(patient)}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onClick(patient)
                }
            }}
        >
            <SharedStack row textAlign="left" spacing={4}>
                <Icon as={FaPaw} boxSize={6} />
                <Box flex={1}>
                    <SharedStack row>
                        <Text fontSize="sm" fontWeight="semibold">
                            <Highlight
                                styles={{
                                    bg: 'brand.100',
                                }}
                                query={value}
                            >
                                {patient.name || ''}
                            </Highlight>
                        </Text>
                        {!patient.is_active && (
                            <Tag size="sm" colorScheme="red">
                                Inactive
                            </Tag>
                        )}
                        {patient.is_deceased && (
                            <Tag size="sm" colorScheme="gray">
                                Deceased
                            </Tag>
                        )}
                    </SharedStack>
                    <SharedStack row spacing={4}>
                        <SharedStack row>
                            <Text
                                fontSize="xs"
                                color={
                                    !patient.clinic_species?.species
                                        ? 'gray.400'
                                        : 'gray.500'
                                }
                            >
                                <Highlight
                                    query={value}
                                    styles={{
                                        bg: 'brand.100',
                                    }}
                                >
                                    {patient.clinic_species?.species || 'N/A'}
                                </Highlight>
                                <Highlight
                                    query={value}
                                    styles={{
                                        bg: 'brand.100',
                                    }}
                                >
                                    {patient.clinic_breed?.description
                                        ? `, ${patient.clinic_breed?.description}`
                                        : 'N/A'}
                                </Highlight>
                            </Text>
                        </SharedStack>
                    </SharedStack>
                </Box>
                <SharedStack row fit spacing={6}>
                    {showReminderPdfAction && (
                        <ExportRemindersButton
                            clinic={clinic}
                            patient={patient}
                        />
                    )}
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                        Owner:{' '}
                        <Highlight
                            query={value}
                            styles={{
                                bg: 'brand.100',
                            }}
                        >
                            {getClientFullName(patient.owner) || 'N/A'}
                        </Highlight>
                        {clientSecondaryName && (
                            <Box as="span" fontSize="sm" color="gray.700">
                                <Highlight
                                    query={value}
                                    styles={{
                                        bg: 'brand.100',
                                    }}
                                >
                                    {` (${clientSecondaryName})`}
                                </Highlight>
                            </Box>
                        )}
                    </Text>
                    <SharedStack fit align="flex-end" spacing={0}>
                        <SharedStack row fit>
                            <Icon
                                as={FaEnvelope}
                                fontSize="xs"
                                color={
                                    !patient.owner?.email
                                        ? 'gray.400'
                                        : 'gray.600'
                                }
                            />
                            <Text
                                fontSize="xs"
                                color={
                                    !patient.owner?.email
                                        ? 'gray.400'
                                        : 'gray.500'
                                }
                            >
                                <Highlight
                                    query={value}
                                    styles={{
                                        bg: 'brand.100',
                                    }}
                                >
                                    {formatEmail(patient.owner?.email) || 'N/A'}
                                </Highlight>
                            </Text>
                        </SharedStack>
                        <SharedStack row fit>
                            <Icon as={FaPhone} fontSize="xs" color="gray.600" />
                            <Text
                                fontSize="xs"
                                color="gray.500"
                                whiteSpace="nowrap"
                            >
                                <Highlight
                                    query={value}
                                    styles={{ bg: 'brand.100' }}
                                >
                                    {formatPhoneReadable(
                                        patient.owner?.phone,
                                        clinic?.address?.country,
                                    ) || ''}
                                </Highlight>
                            </Text>
                        </SharedStack>
                    </SharedStack>
                </SharedStack>
            </SharedStack>
        </Box>
    )
}
